import React, { useState, useEffect, useRef } from 'react';
import ModalLayer from './ModalLayer';
import { supabase } from '../supabaseClient';
import { useTheme } from '../ThemeContext';
import { agentPost } from '../utils/functionAuth';
import { projectSaveMessage } from '../utils/projectNotify';
import { useToast } from './Toast';
import { formatDateOnly } from '../utils/dates';
import { addTaskVendor, removeTaskVendor, taskVendorList, taskVendorOptions } from '../shared/projectTaskVendor';
import { agentCanRemoveTaskPhoto } from '../shared/taskPhotoAccess';
import { VENDOR_LIMIT_MESSAGE } from '../shared/projectTaskLimits';
import ProjectTaskMedia from './ProjectTaskMedia';
import TapToExpand from './TapToExpand';
import { projectPhotoCount, projectSummaryLine } from '../shared/projectCardSummary';
import { deleteAgentTaskPhoto, loadAgentTaskPhotos, uploadAgentTaskPhotos } from '../utils/projectTaskPhotoClient';
import { checklistTemplateFields, checklistTemplateInsert } from '../shared/projectTemplatePayload';
import { checklistWrite, isUuid, saveProjectChecklist } from '../shared/projectChecklistSave';
import { CONNECTION_FAILURE_MESSAGE, RETRYING_MESSAGE, isConnectionFailure } from '../shared/connectionCopy';
import { APP_REFRESH_EVENT } from '../utils/appRefresh';
import { devLog } from '../utils/devLog';

async function postNotify(path, body) {
  try {
    return await agentPost(path, body);
  } catch (error) {
    return { ok: false, error: error.message || 'notification was not sent', data: {} };
  }
}

function pushOutcome(response) {
  if (!response || !response.ok) {
    return { ok: false, error: (response && response.error) || 'notification was not sent' };
  }
  const result = response.data && response.data.push_notification_result;
  if (result && result.ok === false) {
    return { ok: false, error: result.error || 'notification was not sent' };
  }
  return { ok: true };
}

function projectActionIcon(kind) {
  const common = {
    width: 18,
    height: 18,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': 'true',
  };
  if (kind === 'edit') {
    return (
      <svg {...common}>
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <path d="M6 6l12 12" />
      <path d="M18 6L6 18" />
    </svg>
  );
}

const ProjectManager = ({ profile, previewProjects }) => {
  const preview = Array.isArray(previewProjects);
  const { colors } = useTheme();
  const { showToast } = useToast();
  const [projects, setProjects] = useState(preview ? previewProjects : []);
  const [templates, setTemplates] = useState([]);
  const [clients, setClients] = useState([]);
  const [vendors, setVendors] = useState([]); // Add vendors state
  const [showModal, setShowModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    client_id: '',
    due_date: '',
    tasks: []
  });
  const [templateFormData, setTemplateFormData] = useState({
    name: '',
    description: '',
    tasks: []
  });
  const [clientFilter, setClientFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'active', 'completed'
  const [searchTerm, setSearchTerm] = useState('');
  const [regionalVendors, setRegionalVendors] = useState([]);
  const [taskPhotos, setTaskPhotos] = useState({});
  const [photoBusyKey, setPhotoBusyKey] = useState('');
  const [expandedProjects, setExpandedProjects] = useState({});
  const projectsRequest = useRef(0);
  const draftProjectId = useRef(null);

  const beginCreate = () => {
    draftProjectId.current = crypto.randomUUID();
    setEditingProject(null);
    setSelectedTemplate(null);
    setFormData({
      title: '',
      description: '',
      client_id: '',
      due_date: '',
      tasks: [],
    });
    setShowModal(true);
  };

  const closeProjectModal = () => {
    draftProjectId.current = null;
    setShowModal(false);
    setEditingProject(null);
  };

  useEffect(() => {
    if (preview || !profile?.id) return undefined;
    loadProjects();
    loadTemplates();
    loadClients();
    loadVendors();
    loadRegionalVendors();
    return undefined;
  }, [profile, preview]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (preview) return undefined;
    const onRefresh = () => { if (profile?.id) loadProjects(); };
    window.addEventListener(APP_REFRESH_EVENT, onRefresh);
    return () => window.removeEventListener(APP_REFRESH_EVENT, onRefresh);
  }, [profile]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadProjects = async () => {
    const requestId = projectsRequest.current + 1;
    projectsRequest.current = requestId;
    try {
      const { data, error } = await supabase
        .from('projects')
        .select(`
          *,
          clients (
            first_name,
            last_name,
            email
          )
        `)
        .eq('agent_id', profile.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      if (requestId !== projectsRequest.current) return;
      const rows = data || [];
      setProjects(rows);
      try {
        const photos = await loadAgentTaskPhotos(rows.map((project) => project.id));
        if (requestId !== projectsRequest.current) return;
        setTaskPhotos(photos);
      } catch (photoError) {
        console.error('Error loading task photos:', photoError);
        if (requestId !== projectsRequest.current) return;
        setTaskPhotos({});
      }
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  };

  const loadTemplates = async () => {
    try {
      // Load both global templates (agent_id IS NULL) and agent's custom templates
      const { data, error } = await supabase
        .from('project_templates')
        .select('*')
        .or(`agent_id.is.null,agent_id.eq.${profile.id}`)
        .order('is_global', { ascending: false })
        .order('name');

      if (error) throw error;
      devLog('Loaded templates:', data);
      setTemplates(data || []);
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const loadClients = async () => {
    try {
      const { data, error } = await supabase
        .from('clients')
        .select('id, first_name, last_name, email')
        .eq('agent_id', profile.id)
        .eq('status', 'active')
        .order('first_name');

      if (error) throw error;
      setClients(data || []);
    } catch (error) {
      console.error('Error loading clients:', error);
    }
  };

  const loadVendors = async () => {
    try {
      const { data, error } = await supabase
        .from('vendors')
        .select('id, name, category, phone, visibility, team_id, agent_id')
        .eq('agent_id', profile.id)
        .order('name');

      if (error) throw error;
      const mine = data || [];
      let shared = [];
      if (profile.team_id) {
        const teamResult = await supabase
          .from('vendors')
          .select('id, name, phone, visibility, team_id')
          .eq('team_id', profile.team_id)
          .eq('visibility', 'team');
        if (!teamResult.error) shared = teamResult.data || [];
      }
      const byId = new Map();
      [...mine, ...shared].forEach((row) => {
        if (row && row.id) byId.set(row.id, row);
      });
      setVendors([...byId.values()]);
    } catch (error) {
      console.error('Error loading vendors:', error);
    }
  };

  const loadRegionalVendors = async () => {
    try {
      const { data, error } = await supabase
        .from('regional_vendors')
        .select('id, name, phone, region, category, is_active')
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      setRegionalVendors(data || []);
    } catch (error) {
      console.error('Error loading regional vendors:', error);
      setRegionalVendors([]);
    }
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    // Ensure each task has a unique ID
    const tasksWithIds = (template.tasks || []).map((task) => ({
      ...task,
      id: !editingProject && !isUuid(task.id) ? crypto.randomUUID() : (task.id || crypto.randomUUID()),
      vendor_ids: task.vendor_ids || []
    }));
    setFormData({
      ...formData,
      title: template.name,
      description: template.description || '',
      tasks: tasksWithIds
    });
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    // Ensure each task has a unique ID
    const tasksWithIds = (project.tasks || []).map((task) => ({
      ...task,
      id: task.id != null && task.id !== '' ? task.id : crypto.randomUUID(),
      vendor_ids: task.vendor_ids || []
    }));
    setFormData({
      title: project.title,
      description: project.description || '',
      client_id: project.client_id,
      due_date: project.due_date || '',
      tasks: tasksWithIds
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.client_id) {
      showToast('Please select a client');
      return;
    }

    if (formData.tasks.length === 0) {
      showToast('Please add at least one task');
      return;
    }

    const catalog = vendors.concat(regionalVendors);
    const tasks = formData.tasks.map((task) => {
      const attached = taskVendorList(task, catalog);
      return {
        ...task,
        photos: undefined,
        vendors: attached,
        vendor_ids: attached.map((vendor) => vendor.id).filter(Boolean),
        vendor: attached[0] || null,
      };
    });

    const creating = !editingProject;
    const row = checklistWrite({
      creating,
      editing: !creating,
      id: creating ? draftProjectId.current : editingProject.id,
      agentId: profile.id,
      clientId: formData.client_id,
      title: formData.title,
      description: formData.description,
      dueDate: formData.due_date || null,
      tasks,
    });
    if (creating) draftProjectId.current = row.id;

    try {
      let savedProject = null;
      const saved = await saveProjectChecklist(supabase, {
        creating,
        editing: !creating,
        row,
      }, {
        onRetry: () => showToast(RETRYING_MESSAGE, 'info', 4000),
      });
      if (creating) {
        const newProject = saved.data;
        savedProject = newProject;

        const [emailResponse, pushResponse] = await Promise.all([
          postNotify('/.netlify/functions/send-project-notification', {
            projectId: newProject.id
          }),
          postNotify('/.netlify/functions/notify-project-update', {
            project_id: newProject.id,
            homeowner_id: formData.client_id,
            type: 'created'
          }),
        ]);
        showToast(projectSaveMessage({
          created: true,
          email: { ok: emailResponse.ok, error: emailResponse.error || 'email was not sent' },
          push: pushOutcome(pushResponse),
        }));
      } else {
        savedProject = {
          id: editingProject.id,
          title: formData.title,
          description: formData.description,
          client_id: formData.client_id,
          due_date: formData.due_date || null,
          tasks: row.tasks,
        };

        const pushResponse = await postNotify('/.netlify/functions/notify-project-update', {
          project_id: editingProject.id,
          homeowner_id: formData.client_id,
          type: 'updated'
        });
        showToast(projectSaveMessage({ created: false, push: pushOutcome(pushResponse) }));
      }

      draftProjectId.current = null;
      setShowModal(false);
      setEditingProject(null);
      setFormData({
        title: '',
        description: '',
        client_id: '',
        due_date: '',
        tasks: []
      });
      setSelectedTemplate(null);
      if (savedProject && savedProject.id) {
        const savedId = savedProject.id;
        projectsRequest.current += 1;
        setProjects((current) => {
          const rest = current.filter((project) => project.id !== savedId);
          const previous = current.find((project) => project.id === savedId) || {};
          return [{ ...previous, ...savedProject }, ...rest];
        });
      }
      await loadProjects();
    } catch (error) {
      console.error('Error saving project:', error);
      const message = error && error.message ? String(error.message) : '';
      if (message.indexOf('at most 3') !== -1) showToast(VENDOR_LIMIT_MESSAGE);
      else if (message.indexOf('same vendor') !== -1) showToast(message);
      else if (isConnectionFailure(error)) showToast(CONNECTION_FAILURE_MESSAGE, 'error');
      else showToast('Error saving project. Please try again.');
    }
  };

  const addTask = () => {
    const newTask = {
      id: crypto.randomUUID(),
      title: '',
      completed: false,
      notes: '',
      vendor_ids: [],
      vendors: [],
      vendor: null
    };
    setFormData({
      ...formData,
      tasks: [...formData.tasks, newTask]
    });
  };

  const updateTask = (taskId, field, value) => {
    setFormData({
      ...formData,
      tasks: formData.tasks.map(task =>
        task.id === taskId ? { ...task, [field]: value } : task
      )
    });
  };

  const removeTask = (taskId) => {
    setFormData({
      ...formData,
      tasks: formData.tasks.filter(task => task.id !== taskId)
    });
  };

  const vendorOptionList = () => {
    const choices = taskVendorOptions(vendors, regionalVendors);
    return [...choices.agent, ...choices.regional].map((vendor) => ({
      value: `${vendor.source}:${vendor.id}`,
      label: vendor.label,
      vendor,
    }));
  };

  const changeTaskVendors = (taskId, nextTask, error) => {
    if (error) {
      showToast(error);
      return;
    }
    setFormData({
      ...formData,
      tasks: formData.tasks.map((task) => (task.id === taskId ? nextTask : task)),
    });
  };

  const loadedVendorRows = () => vendors.concat(regionalVendors);

  const addVendorToTask = (taskId, value) => {
    const option = vendorOptionList().find((item) => item.value === value);
    const task = formData.tasks.find((item) => item.id === taskId);
    if (!task || !option) return;
    const added = addTaskVendor(task, option.vendor, loadedVendorRows());
    changeTaskVendors(taskId, added.task, added.error);
  };

  const removeVendorFromTask = (taskId, vendor) => {
    const task = formData.tasks.find((item) => item.id === taskId);
    if (!task) return;
    changeTaskVendors(taskId, removeTaskVendor(task, vendor.source, vendor.id, loadedVendorRows()), '');
  };

  const photosFor = (projectId, taskId, projectAgentId) => (
    (taskPhotos[`${projectId}:${taskId}`] || []).map((photo) => ({
      ...photo,
      removable: agentCanRemoveTaskPhoto(photo, profile && profile.id, projectAgentId),
    }))
  );

  const addPhotoToTask = async (projectId, taskId, fileOrFiles, error) => {
    if (error) {
      showToast(error.message || 'Could not use that photo.');
      return;
    }
    const files = (Array.isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles]).filter(Boolean);
    if (!files.length || !projectId) return;
    const key = `${projectId}:${taskId}`;
    setPhotoBusyKey(key);
    try {
      await uploadAgentTaskPhotos(projectId, taskId, files, profile && profile.id, (photo) => {
        setTaskPhotos((current) => ({
          ...current,
          [key]: (current[key] || []).concat([photo]),
        }));
      });
    } catch (uploadError) {
      console.error('Error uploading task photo:', uploadError);
      const message = uploadError && uploadError.message ? uploadError.message : '';
      showToast(message.indexOf('at most 12') !== -1 ? message : 'Could not upload that photo.');
    } finally {
      setPhotoBusyKey('');
    }
  };

  const removePhotoFromTask = async (projectId, taskId, photo) => {
    if (!photo || photo.removable === false) return;
    if (photo.uploaded_by_role === 'homeowner') return;
    const key = `${projectId}:${taskId}`;
    try {
      await deleteAgentTaskPhoto(photo);
      setTaskPhotos((current) => ({
        ...current,
        [key]: (current[key] || []).filter((item) => item.id !== photo.id),
      }));
    } catch (removeError) {
      console.error('Error removing task photo:', removeError);
      showToast('Could not remove that photo.');
    }
  };

  const deleteProject = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', projectId);

      if (error) throw error;
      loadProjects();
    } catch (error) {
      console.error('Error deleting project:', error);
      showToast('Error deleting project');
    }
  };

  const getCompletionPercentage = (project) => {
    if (!project.tasks || project.tasks.length === 0) return 0;
    const completed = project.tasks.filter(t => t.completed).length;
    return Math.round((completed / project.tasks.length) * 100);
  };

  const saveAsTemplate = async () => {
    if (!formData.title) {
      showToast('Please enter a project title first');
      return;
    }

    if (formData.tasks.length === 0) {
      showToast('Please add at least one task');
      return;
    }

    const templateName = prompt('Enter a name for this template:', formData.title);
    if (!templateName) return;

    try {
      const { error } = await supabase
        .from('project_templates')
        .insert([checklistTemplateInsert(profile, {
          name: templateName,
          description: formData.description || '',
          tasks: formData.tasks.map(t => ({ title: t.title, completed: false, notes: '' })),
        })]);

      if (error) throw error;

      showToast('Template saved successfully!', 'success');
      loadTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      showToast('Error saving template', 'error');
    }
  };

  const blankTemplateForm = () => ({ name: '', description: '', tasks: [] });

  const closeTemplateModal = () => {
    setShowTemplateModal(false);
    setEditingTemplateId(null);
    setTemplateFormData(blankTemplateForm());
  };

  const openNewTemplate = () => {
    setEditingTemplateId(null);
    setTemplateFormData(blankTemplateForm());
    setShowTemplateModal(true);
  };

  const openTemplateEditor = (template) => {
    if (!template || !profile || template.agent_id !== profile.id) return;
    setEditingTemplateId(template.id);
    setTemplateFormData({
      name: template.name || '',
      description: template.description || '',
      tasks: (Array.isArray(template.tasks) ? template.tasks : []).map((task, index) => ({
        id: task.id || `${template.id}-${index}`,
        title: task.title || '',
        completed: false,
        notes: task.notes || '',
      })),
    });
    setShowTemplateModal(true);
  };

  const handleSaveCustomTemplate = async (e) => {
    e.preventDefault();

    const tasks = templateFormData.tasks
      .map((task) => ({ ...task, title: String(task.title || '').trim() }))
      .filter((task) => task.title);
    if (!String(templateFormData.name || '').trim()) {
      showToast('Please enter a template name', 'warning');
      return;
    }

    if (tasks.length === 0) {
      showToast('Please add at least one task', 'warning');
      return;
    }

    const fields = { ...templateFormData, tasks };
    try {
      const { error } = editingTemplateId
        ? await supabase
          .from('project_templates')
          .update(checklistTemplateFields(fields))
          .eq('id', editingTemplateId)
          .eq('agent_id', profile.id)
        : await supabase
          .from('project_templates')
          .insert([checklistTemplateInsert(profile, fields)]);

      if (error) throw error;

      showToast(editingTemplateId ? 'Template saved.' : 'Custom template created successfully!', 'success');
      closeTemplateModal();
      loadTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      showToast(editingTemplateId ? 'Error saving template' : 'Error creating template', 'error');
    }
  };

  const addTemplateTask = () => {
    const newTask = {
      id: Date.now(),
      title: '',
      completed: false,
      notes: ''
    };
    setTemplateFormData({
      ...templateFormData,
      tasks: [...templateFormData.tasks, newTask]
    });
  };

  const updateTemplateTask = (taskId, value) => {
    setTemplateFormData({
      ...templateFormData,
      tasks: templateFormData.tasks.map(task =>
        task.id === taskId ? { ...task, title: value } : task
      )
    });
  };

  const removeTemplateTask = (taskId) => {
    setTemplateFormData({
      ...templateFormData,
      tasks: templateFormData.tasks.filter(task => task.id !== taskId)
    });
  };

  const deleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('project_templates')
        .delete()
        .eq('id', templateId)
        .eq('agent_id', profile.id); // Only allow deleting own templates

      if (error) throw error;

      showToast('Template deleted', 'success');
      loadTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      showToast('Error deleting template', 'error');
    }
  };

  return (
    <div>
      <div style={styles.header}>
        <h2 data-tour="projects" style={{...styles.pageTitle, color: colors.text}}>Project Checklists</h2>
        <div style={{display: 'flex', gap: '0.5rem', flexWrap: 'wrap'}}>
          <button style={styles.btnPrimary} onClick={beginCreate}>
            New project
          </button>
        </div>
      </div>

      {/* Templates Section */}
      <div style={{...styles.card, background: colors.surface, border: `1px solid ${colors.border}`, marginBottom: '1.5rem'}}>
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem'}}>
          <h3 style={{fontSize: '1.2rem', margin: 0, color: colors.text}}>My Templates</h3>
          {templates.length > 0 && (
            <button type="button" style={styles.btnSecondary} onClick={openNewTemplate}>
              New template
            </button>
          )}
        </div>
        {templates.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '3rem 2rem',
            color: colors.textSecondary
          }}>
            <div style={{fontSize: '1.2rem', fontWeight: 600, color: colors.text, marginBottom: '0.75rem'}}>
              No Templates Yet
            </div>
            <div style={{fontSize: '1rem', lineHeight: '1.6', maxWidth: '450px', margin: '0 auto 1.5rem'}}>
              Create reusable project templates to quickly assign common checklists to clients.
            </div>
            <button style={styles.btnPrimary} onClick={openNewTemplate}>
              Create Your First Template
            </button>
          </div>
        ) : (
          <div style={styles.templateGrid}>
            {templates.map(template => {
              const owned = template.agent_id === profile.id;
              return (
              <div
                key={template.id}
                style={{...styles.templateCardLarge, background: colors.surfaceAlt, border: `2px solid ${colors.border}`, cursor: owned ? 'pointer' : 'default'}}
                onClick={() => openTemplateEditor(template)}
              >
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem', minWidth: 0}}>
                  <div style={{flex: 1, minWidth: 0}}>
                    <h4 style={{margin: '0 0 0.5rem 0', color: colors.text, overflowWrap: 'anywhere', wordBreak: 'break-word'}}>{template.name}</h4>
                    <p style={{fontSize: '0.85rem', color: colors.textSecondary, margin: '0 0 0.5rem 0'}}>
                      {template.tasks?.length || 0} tasks
                    </p>
                    {template.agent_id === null && (
                      <span style={{
                        fontSize: '0.75rem',
                        background: '#10b981',
                        color: 'white',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '0.25rem',
                        fontWeight: '600'
                      }}>
                        Pacarion Starter Template
                      </span>
                    )}
                  </div>
                  {owned && (
                    <div style={{display: 'flex', gap: '0.4rem', flex: '0 0 auto'}} onClick={(event) => event.stopPropagation()}>
                      <button
                        type="button"
                        style={{...styles.btnSecondary, padding: '0.4rem 0.75rem', fontSize: '0.85rem'}}
                        onClick={() => openTemplateEditor(template)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        style={styles.deleteBtn}
                        onClick={() => deleteTemplate(template.id)}
                        title="Delete template"
                        aria-label={`Delete ${template.name}`}
                      >
                        ✕
                      </button>
                    </div>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>

      <div style={{...styles.card, background: colors.surface, border: `1px solid ${colors.border}`}}>
        <p style={{color: colors.textSecondary, marginBottom: '1.5rem'}}>
          Create custom project checklists (like pre-listing tasks) and assign them to specific clients.
        </p>

        {/* Filters */}
        {projects.length > 0 && (
          <div style={{marginBottom: '2rem', display: 'flex', flexDirection: 'column', gap: '1rem'}}>
            {/* Search Bar */}
            <input
              type="text"
              placeholder="🔍 Search projects by title..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                padding: '0.75rem 1rem',
                fontSize: '1rem',
                border: `2px solid ${colors.border}`,
                borderRadius: '0.5rem',
                background: colors.surfaceAlt,
                color: colors.text,
                width: '100%',
                boxSizing: 'border-box'
              }}
            />

            {/* Filter Dropdowns */}
            <div style={{display: 'flex', gap: '1rem', flexWrap: 'wrap'}}>
              {/* Client Filter */}
              <select
                value={clientFilter}
                onChange={(e) => setClientFilter(e.target.value)}
                style={{
                  padding: '0.75rem 1rem',
                  fontSize: '0.95rem',
                  border: `2px solid ${colors.border}`,
                  borderRadius: '0.5rem',
                  background: colors.surfaceAlt,
                  color: colors.text,
                  cursor: 'pointer',
                  flex: 1,
                  minWidth: '200px'
                }}
              >
                <option value="all">All Clients ({projects.length})</option>
                {clients.map(client => {
                  const clientProjects = projects.filter(p => p.client_id === client.id);
                  return (
                    <option key={client.id} value={client.id}>
                      {client.first_name} {client.last_name} ({clientProjects.length})
                    </option>
                  );
                })}
              </select>

              {/* Status Filter */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{
                  padding: '0.75rem 1rem',
                  fontSize: '0.95rem',
                  border: `2px solid ${colors.border}`,
                  borderRadius: '0.5rem',
                  background: colors.surfaceAlt,
                  color: colors.text,
                  cursor: 'pointer',
                  flex: 1,
                  minWidth: '200px'
                }}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
              </select>
            </div>
          </div>
        )}

        {projects.length === 0 ? (
          <div style={{...styles.emptyState, color: colors.textSecondary}}>
            No project checklists yet. Use New project to get started.
          </div>
        ) : (
          <div style={styles.projectGrid}>
            {projects.filter(project => {
              // Filter by client
              if (clientFilter !== 'all' && project.client_id !== clientFilter) {
                return false;
              }

              // Filter by status
              if (statusFilter !== 'all') {
                const completion = getCompletionPercentage(project);
                if (statusFilter === 'completed' && completion !== 100) return false;
                if (statusFilter === 'active' && completion === 100) return false;
              }

              // Filter by search term
              if (searchTerm && !project.title.toLowerCase().includes(searchTerm.toLowerCase())) {
                return false;
              }

              return true;
            }).map(project => {
              const completion = getCompletionPercentage(project);
              const client = project.clients;
              const tasks = project.tasks || [];
              const photoCount = projectPhotoCount(tasks, (task) => (
                (taskPhotos[`${project.id}:${task.id}`] || []).length
              ));
              const compactActions = window.innerWidth < 768;
              const actionStyle = compactActions ? styles.cardAction : null;
              const expanded = expandedProjects[project.id] === true;

              return (
                <article key={project.id} aria-label={project.title} style={{...styles.projectCard, background: colors.surfaceAlt, border: `1px solid ${colors.border}`}}>
                  <TapToExpand
                    expanded={expanded}
                    onToggle={() => setExpandedProjects((prev) => ({ ...prev, [project.id]: !expanded }))}
                    controls={`project-${project.id}`}
                    summary={(
                      <>
                      <span style={{
                        display: 'block',
                        fontSize: styles.projectTitle.fontSize,
                        fontWeight: 650,
                        lineHeight: 1.3,
                        color: colors.text,
                        overflowWrap: 'break-word',
                      }}>
                        {project.title}
                      </span>
                      <span style={{
                        display: 'block',
                        marginTop: '0.2rem',
                        color: colors.primary,
                        fontSize: styles.projectClient.fontSize,
                        fontWeight: 650,
                        overflowWrap: 'anywhere',
                      }}>
                        {client ? `${client.first_name} ${client.last_name}` : 'Unassigned'}
                      </span>
                      <span style={{
                        display: 'block',
                        marginTop: '0.25rem',
                        color: colors.textSecondary,
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                      }}>
                        {projectSummaryLine(tasks, photoCount)}
                      </span>
                      </>
                    )}
                    trailing={(
                    <div style={{
                      display: 'flex',
                      gap: compactActions ? '0.35rem' : '0.75rem',
                      alignItems: 'flex-start',
                      flexShrink: 0
                    }}>
                      <button
                        type="button"
                        className={compactActions ? 'project-card-action' : undefined}
                        style={compactActions ? { ...actionStyle, background: styles.editBtn.background } : styles.editBtn}
                        aria-label={compactActions ? `Edit ${project.title}` : undefined}
                        onClick={() => handleEdit(project)}
                      >
                        {compactActions ? projectActionIcon('edit') : 'Edit'}
                      </button>
                      <button
                        type="button"
                        className={compactActions ? 'project-card-action' : undefined}
                        style={compactActions ? { ...actionStyle, background: styles.deleteBtn.background } : styles.deleteBtn}
                        aria-label={`Delete ${project.title}`}
                        onClick={() => deleteProject(project.id)}
                      >
                        {compactActions ? projectActionIcon('delete') : '✕'}
                      </button>
                    </div>
                    )}
                  >
                    {project.description && (
                      <p style={{...styles.projectDescription, color: colors.textSecondary}}>{project.description}</p>
                    )}

                    {project.due_date && (
                      <p style={styles.projectDueDate}>
                        Due: {formatDateOnly(project.due_date)}
                      </p>
                    )}

                    <div style={{...styles.progressBar, background: colors.border}}>
                      <div
                        style={{
                          ...styles.progressFill,
                          width: `${completion}%`,
                          background: completion === 100 ? '#10b981' : colors.primary
                        }}
                      />
                    </div>
                    <p style={{...styles.progressText, color: colors.textSecondary}}>
                      {completion}% Complete ({tasks.filter(t => t.completed).length} of {tasks.length} tasks)
                    </p>

                    <div style={styles.taskList}>
                      {tasks.map((task, index) => (
                        <div key={task.id} style={{
                          ...styles.taskItem,
                          alignItems: 'flex-start',
                          flexDirection: 'column',
                          borderBottom: index === tasks.length - 1 ? 'none' : styles.taskItem.borderBottom
                        }}>
                          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', width: '100%' }}>
                            <span style={{
                              fontSize: '1.25rem',
                              color: task.completed ? '#10b981' : colors.textSecondary,
                              lineHeight: '1'
                            }}>
                              {task.completed ? '✓' : '○'}
                            </span>
                            <span style={{
                              color: task.completed ? '#10b981' : colors.text,
                              textDecoration: task.completed ? 'line-through' : 'none',
                              flex: 1
                            }}>
                              {task.title}
                            </span>
                          </div>
                          <ProjectTaskMedia
                            colors={colors}
                            brandColor={colors.primary}
                            vendors={taskVendorList(task, loadedVendorRows())}
                            photos={photosFor(project.id, task.id, project.agent_id)}
                            canAddPhoto
                            photoBusy={photoBusyKey === `${project.id}:${task.id}`}
                            onPhotoFile={(file, photoError) => addPhotoToTask(project.id, task.id, file, photoError)}
                            onRemovePhoto={(photo) => removePhotoFromTask(project.id, task.id, photo)}
                          />
                        </div>
                      ))}
                    </div>
                  </TapToExpand>
                </article>
              );
            })}
          </div>
        )}
      </div>

      {/* Create/Edit Project Modal */}
      {showModal && (
        <ModalLayer className="app-modal-layer" style={styles.modal} onClick={closeProjectModal}>
          <div className="app-modal-card" style={{...styles.modalContent, background: colors.surface, border: `1px solid ${colors.border}`}} onClick={(e) => e.stopPropagation()}>
            <div className="app-modal-header" style={{...styles.modalHeader, background: colors.surface}}>
              <h2 style={{color: colors.text, margin: 0}}>{editingProject ? 'Edit Project Checklist' : 'Create Project Checklist'}</h2>
              <button type="button" className="app-modal-close" aria-label="Close" style={{...styles.closeBtn, color: colors.text}} onClick={closeProjectModal}>
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} style={{ minWidth: 0, maxWidth: '100%' }}>
              {/* Template Selection Dropdown */}
              {templates.length > 0 && !editingProject && (
                <div style={styles.formGroup}>
                  <label style={{...styles.label, color: colors.text}}>Start from template (optional)</label>
                  <select
                    value={selectedTemplate?.id || ''}
                    onChange={(e) => {
                      const template = templates.find(t => t.id === e.target.value);
                      if (template) {
                        handleTemplateSelect(template);
                      } else {
                        setSelectedTemplate(null);
                        setFormData({...formData, title: '', description: '', tasks: []});
                      }
                    }}
                    style={{...styles.input, background: colors.surfaceAlt, border: `2px solid ${colors.border}`, color: colors.text}}
                  >
                    <option value="">No template - start from scratch</option>
                    {templates.map(template => (
                      <option key={template.id} value={template.id}>
                        {template.name} ({template.tasks?.length || 0} tasks)
                        {template.agent_id === null ? ' - Pacarion Starter Template' : ''}
                      </option>
                    ))}
                  </select>
                  {selectedTemplate && (
                    <p style={{fontSize: '0.85rem', color: colors.primary, marginTop: '0.5rem'}}>
                      Template loaded: {selectedTemplate.name}
                    </p>
                  )}
                </div>
              )}

              <div style={styles.formGroup}>
                <label style={{...styles.label, color: colors.text}}>Project Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  style={{...styles.input, background: colors.surfaceAlt, border: `2px solid ${colors.border}`, color: colors.text}}
                  placeholder="e.g., Pre-Listing Preparation"
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={{...styles.label, color: colors.text}}>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  style={{...styles.input, background: colors.surfaceAlt, border: `2px solid ${colors.border}`, color: colors.text, minHeight: '80px'}}
                  placeholder="Optional description..."
                />
              </div>

              <div style={styles.formGroup}>
                <label style={{...styles.label, color: colors.text}}>Assign to Client *</label>
                <select
                  value={formData.client_id}
                  onChange={(e) => setFormData({...formData, client_id: e.target.value})}
                  style={{...styles.input, background: colors.surfaceAlt, border: `2px solid ${colors.border}`, color: colors.text}}
                  required
                >
                  <option value="">Select a client...</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.first_name} {client.last_name} ({client.email})
                    </option>
                  ))}
                </select>
              </div>

              <div style={{...styles.formGroup, minWidth: 0, maxWidth: '100%', width: '100%', overflow: 'hidden'}}>
                <label style={{...styles.label, color: colors.text}}>Due Date (Optional)</label>
                <input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                  style={{...styles.input, background: colors.surfaceAlt, border: `2px solid ${colors.border}`, color: colors.text, display: 'block', width: '100%', minWidth: 0, maxWidth: '100%', boxSizing: 'border-box'}}
                />
              </div>

              <div style={{...styles.formGroup, minWidth: 0, maxWidth: '100%'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', gap: '0.5rem', flexWrap: 'wrap'}}>
                  <label style={{...styles.label, color: colors.text}}>Tasks *</label>
                  <button type="button" style={styles.addTaskBtn} onClick={addTask}>
                    + Add Task
                  </button>
                </div>

                {formData.tasks.map((task, index) => (
                  <div key={task.id} style={{marginBottom: '0.55rem', padding: '0.55rem', background: colors.surfaceAlt, borderRadius: '0.5rem', border: `1px solid ${colors.border}`, minWidth: 0, maxWidth: '100%', boxSizing: 'border-box'}}>
                    <div style={styles.taskInputRow}>
                      <span style={{color: colors.text, fontWeight: '600', minWidth: '1.4rem', paddingTop: '0.55rem'}}>
                        {index + 1}.
                      </span>
                      <textarea
                        value={task.title}
                        rows={1}
                        onChange={(e) => updateTask(task.id, 'title', e.target.value)}
                        ref={(node) => {
                          if (!node) return;
                          node.style.height = 'auto';
                          node.style.height = `${node.scrollHeight}px`;
                        }}
                        style={{...styles.input, background: colors.surface, border: `2px solid ${colors.border}`, color: colors.text, flex: '1 1 auto', minWidth: 0, width: '100%', maxWidth: '100%', marginBottom: 0, resize: 'none', overflow: 'hidden', minHeight: '44px', fontSize: '16px', lineHeight: 1.35}}
                        placeholder="Task description..."
                        required
                      />
                      <button
                        type="button"
                        aria-label="Delete task"
                        style={styles.removeTaskBtn}
                        onClick={() => removeTask(task.id)}
                      >
                        ✕
                      </button>
                    </div>

                    <ProjectTaskMedia
                      colors={colors}
                      brandColor={colors.primary}
                      vendors={taskVendorList(task, loadedVendorRows())}
                      photos={editingProject ? photosFor(editingProject.id, task.id, editingProject.agent_id) : []}
                      vendorOptions={vendorOptionList()}
                      canEditVendors
                      canAddPhoto={Boolean(editingProject)}
                      photoBusy={editingProject && photoBusyKey === `${editingProject.id}:${task.id}`}
                      onAddVendor={(value) => addVendorToTask(task.id, value)}
                      onRemoveVendor={(vendor) => removeVendorFromTask(task.id, vendor)}
                      onPhotoFile={(file, photoError) => {
                        if (!editingProject) return;
                        addPhotoToTask(editingProject.id, task.id, file, photoError);
                      }}
                      onRemovePhoto={(photo) => removePhotoFromTask(editingProject.id, task.id, photo)}
                    />
                    {!editingProject && (
                      <p style={{ margin: '0.5rem 0 0', color: colors.textSecondary, fontSize: '0.85rem' }}>
                        Save the project, then add photos to this task.
                      </p>
                    )}
                  </div>
                ))}

                {formData.tasks.length === 0 && (
                  <p style={{color: colors.textSecondary, fontSize: '0.9rem'}}>
                    No tasks yet. Click "Add Task" to get started.
                  </p>
                )}
              </div>

              <div className="app-modal-actions" style={{background: colors.surface}}>
                <button type="submit" style={styles.btnPrimary}>
                  {editingProject ? 'Save Changes' : 'Create & Send to Client'}
                </button>
                {!editingProject && formData.tasks.length > 0 && (
                  <button
                    type="button"
                    style={{...styles.btnPrimary, background: '#10b981'}}
                    onClick={saveAsTemplate}
                  >
                    Save as Template
                  </button>
                )}
                <button
                  type="button"
                  style={{...styles.btnPrimary, background: '#666'}}
                  onClick={() => {
                    closeProjectModal();
                    setSelectedTemplate(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </ModalLayer>
      )}

      {/* Create Template Modal */}
      {showTemplateModal && (
        <ModalLayer className="app-modal-layer" style={styles.modal} onClick={closeTemplateModal}>
          <div className="app-modal-card" style={{...styles.modalContent, background: colors.surface, border: `1px solid ${colors.border}`}} onClick={(e) => e.stopPropagation()}>
            <div className="app-modal-header" style={{...styles.modalHeader, background: colors.surface}}>
              <h2 style={{color: colors.text, margin: 0}}>{editingTemplateId ? 'Edit template' : 'Create Custom Template'}</h2>
              <button type="button" className="app-modal-close" aria-label="Close" style={{...styles.closeBtn, color: colors.text}} onClick={closeTemplateModal}>
                ✕
              </button>
            </div>

            <form onSubmit={handleSaveCustomTemplate}>
              <div style={styles.formGroup}>
                <label style={{...styles.label, color: colors.text}}>Template Name *</label>
                <input
                  type="text"
                  value={templateFormData.name}
                  onChange={(e) => setTemplateFormData({...templateFormData, name: e.target.value})}
                  style={{...styles.input, background: colors.surfaceAlt, border: `2px solid ${colors.border}`, color: colors.text}}
                  placeholder="e.g., My Listing Prep Checklist"
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={{...styles.label, color: colors.text}}>Description (Optional)</label>
                <textarea
                  value={templateFormData.description}
                  onChange={(e) => setTemplateFormData({...templateFormData, description: e.target.value})}
                  style={{...styles.input, background: colors.surfaceAlt, border: `2px solid ${colors.border}`, color: colors.text, minHeight: '60px'}}
                  placeholder="What is this template for?"
                />
              </div>

              <div style={styles.formGroup}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem'}}>
                  <label style={{...styles.label, color: colors.text}}>Tasks *</label>
                  <button type="button" style={styles.addTaskBtn} onClick={addTemplateTask}>
                    + Add Task
                  </button>
                </div>

                {templateFormData.tasks.map((task, index) => (
                  <div key={task.id} style={styles.taskInputRow}>
                    <span style={{color: colors.text, fontWeight: '600', minWidth: '1.4rem', paddingTop: '0.55rem'}}>
                      {index + 1}.
                    </span>
                    <textarea
                      value={task.title}
                      rows={1}
                      onChange={(e) => updateTemplateTask(task.id, e.target.value)}
                      ref={(node) => {
                        if (!node) return;
                        node.style.height = 'auto';
                        node.style.height = `${node.scrollHeight}px`;
                      }}
                      style={{...styles.input, background: colors.surfaceAlt, border: `2px solid ${colors.border}`, color: colors.text, flex: '1 1 auto', minWidth: 0, width: '100%', maxWidth: '100%', marginBottom: 0, resize: 'none', overflow: 'hidden', minHeight: '44px', fontSize: '16px', lineHeight: 1.35}}
                      placeholder="Task description..."
                      required
                    />
                    <button
                      type="button"
                      style={styles.removeTaskBtn}
                      onClick={() => removeTemplateTask(task.id)}
                    >
                      ✕
                    </button>
                  </div>
                ))}

                {templateFormData.tasks.length === 0 && (
                  <p style={{color: colors.textSecondary, fontSize: '0.9rem'}}>
                    No tasks yet. Click "Add Task" to get started.
                  </p>
                )}
              </div>

              <div className="app-modal-actions" style={{background: colors.surface}}>
                <button type="submit" style={styles.btnPrimary}>
                  {editingTemplateId ? 'Save template' : 'Create Template'}
                </button>
                <button
                  type="button"
                  style={{...styles.btnPrimary, background: '#666'}}
                  onClick={closeTemplateModal}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </ModalLayer>
      )}
    </div>
  );
};

const styles = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem',
    flexWrap: 'wrap',
    gap: '1rem'
  },
  pageTitle: {
    fontSize: 'clamp(1.3rem, 5vw, 1.8rem)',
    margin: 0
  },
  card: {
    background: 'white',
    padding: window.innerWidth < 768 ? '1rem' : '2rem',
    borderRadius: '1rem',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    width: '100%',
    boxSizing: 'border-box'
  },
  emptyState: {
    textAlign: 'center',
    color: '#666',
    padding: '3rem 1rem'
  },
  projectGrid: {
    display: 'grid',
    gridTemplateColumns: window.innerWidth < 768 ? '1fr' : 'repeat(auto-fill, minmax(450px, 1fr))',
    gap: '1.5rem',
    width: '100%'
  },
  projectCard: {
    background: '#f9f9f9',
    padding: window.innerWidth < 768 ? '1.25rem' : '1.5rem',
    borderRadius: window.innerWidth < 768 ? '1.25rem' : '0.75rem',
    border: '1px solid #e0e0e0',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
  },
  projectHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '1.25rem',
    paddingBottom: '1rem',
    borderBottom: '1px solid #e0e0e0'
  },
  projectTitle: {
    margin: '0 0 0.5rem 0',
    fontSize: window.innerWidth < 768 ? '1.1rem' : '1.3rem',
    color: '#333',
    fontWeight: '600',
    lineHeight: '1.3'
  },
  projectClient: {
    margin: 0,
    color: '#59c4c4',
    fontSize: window.innerWidth < 768 ? '0.85rem' : '0.95rem',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  },
  projectDescription: {
    color: '#666',
    fontSize: window.innerWidth < 768 ? '0.85rem' : '0.9rem',
    marginBottom: '1.25rem',
    lineHeight: '1.6',
    padding: '0.75rem',
    background: 'rgba(89, 196, 196, 0.05)',
    borderRadius: '0.5rem',
    borderLeft: '3px solid #59c4c4'
  },
  projectDueDate: {
    color: '#f59e0b',
    fontSize: window.innerWidth < 768 ? '0.8rem' : '0.85rem',
    fontWeight: '600',
    marginBottom: '1.25rem',
    padding: '0.5rem 0.75rem',
    background: 'rgba(245, 158, 11, 0.1)',
    borderRadius: '0.5rem',
    display: 'inline-block'
  },
  progressBar: {
    width: '100%',
    height: window.innerWidth < 768 ? '10px' : '12px',
    background: '#e0e0e0',
    borderRadius: '6px',
    overflow: 'hidden',
    marginBottom: '0.75rem',
    boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)'
  },
  progressFill: {
    height: '100%',
    transition: 'width 0.5s ease',
    background: 'linear-gradient(90deg, #59c4c4, #4ab3b3)'
  },
  progressText: {
    fontSize: window.innerWidth < 768 ? '0.8rem' : '0.9rem',
    color: '#666',
    marginBottom: '1.25rem',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  },
  taskList: {
    marginTop: '1.25rem',
    background: 'rgba(0,0,0,0.02)',
    padding: '1rem',
    borderRadius: '0.75rem',
    border: '1px solid #e0e0e0'
  },
  taskItem: {
    padding: '0.75rem 0',
    fontSize: window.innerWidth < 768 ? '0.85rem' : '0.95rem',
    borderBottom: '1px solid rgba(0,0,0,0.05)',
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    lineHeight: '1.5'
  },
  cardAction: {
    width: '44px',
    height: '44px',
    minWidth: '44px',
    minHeight: '44px',
    padding: 0,
    border: 'none',
    borderRadius: '0.65rem',
    color: 'white',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: '0 0 44px',
    boxSizing: 'border-box',
    boxShadow: 'none'
  },
  editBtn: {
    background: 'linear-gradient(135deg, #59c4c4 0%, #4ab3b3 100%)',
    color: 'white',
    border: 'none',
    padding: window.innerWidth < 768 ? '0.6rem 1rem' : '0.5rem 0.9rem',
    borderRadius: window.innerWidth < 768 ? '1.25rem' : '0.5rem',
    cursor: 'pointer',
    fontSize: window.innerWidth < 768 ? '0.85rem' : '0.9rem',
    fontWeight: '600',
    minHeight: window.innerWidth < 768 ? '44px' : 'auto',
    boxShadow: '0 2px 4px rgba(89, 196, 196, 0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  deleteBtn: {
    background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
    color: 'white',
    border: 'none',
    padding: window.innerWidth < 768 ? '0.6rem 0.9rem' : '0.5rem 0.75rem',
    borderRadius: window.innerWidth < 768 ? '1.25rem' : '0.5rem',
    cursor: 'pointer',
    fontSize: window.innerWidth < 768 ? '1rem' : '0.9rem',
    fontWeight: '600',
    minHeight: window.innerWidth < 768 ? '44px' : 'auto',
    minWidth: window.innerWidth < 768 ? '44px' : 'auto',
    boxShadow: '0 2px 4px rgba(220, 53, 69, 0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  btnPrimary: {
    background: 'linear-gradient(135deg, #59c4c4 0%, #4ab3b3 100%)',
    color: 'white',
    border: 'none',
    padding: window.innerWidth < 768 ? '0.65rem 1rem' : '0.75rem 1.5rem',
    borderRadius: '0.5rem',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: 'clamp(0.85rem, 3vw, 1rem)',
    whiteSpace: 'nowrap'
  },
  modal: {
    display: 'flex',
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.7)',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: window.innerWidth < 768 ? '1rem' : '0'
  },
  modalContent: {
    background: 'white',
    padding: window.innerWidth < 768 ? '1.5rem' : '2rem',
    borderRadius: '1rem',
    maxWidth: '700px',
    width: window.innerWidth < 768 ? '100%' : '90%',
    maxHeight: '90vh',
    overflowY: 'auto'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem'
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '1.5rem',
    cursor: 'pointer',
    color: '#666'
  },
  formGroup: {
    marginBottom: '1.25rem'
  },
  label: {
    display: 'block',
    fontWeight: 600,
    marginBottom: '0.5rem',
    color: '#333'
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    border: '2px solid #ddd',
    borderRadius: '0.5rem',
    fontSize: '1rem',
    boxSizing: 'border-box'
  },
  templateGrid: {
    display: 'grid',
    gridTemplateColumns: window.innerWidth < 768 ? '1fr' : 'repeat(auto-fill, minmax(250px, 1fr))',
    gap: '1rem',
    marginTop: '0.5rem',
    width: '100%'
  },
  templateCard: {
    padding: '1rem',
    background: '#f9f9f9',
    border: '2px solid #ddd',
    borderRadius: '0.5rem',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'all 0.2s'
  },
  taskInputRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.4rem',
    marginBottom: '0.35rem',
    minWidth: 0,
    maxWidth: '100%'
  },
  addTaskBtn: {
    background: '#10b981',
    color: 'white',
    border: 'none',
    padding: '0.5rem 1rem',
    borderRadius: '0.5rem',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: '600'
  },
  removeTaskBtn: {
    background: 'transparent',
    color: '#dc3545',
    border: '1px solid #dc3545',
    minWidth: '44px',
    minHeight: '44px',
    padding: '0.35rem',
    borderRadius: '0.5rem',
    cursor: 'pointer',
    fontSize: '1rem',
    flex: '0 0 auto'
  },
  btnSecondary: {
    background: 'white',
    color: '#59c4c4',
    border: '2px solid #59c4c4',
    padding: window.innerWidth < 768 ? '0.65rem 1rem' : '0.75rem 1.5rem',
    borderRadius: '0.5rem',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: 'clamp(0.85rem, 3vw, 1rem)',
    whiteSpace: 'nowrap'
  },
  templateCardLarge: {
    padding: '1.25rem',
    background: '#f9f9f9',
    border: '2px solid #ddd',
    borderRadius: '0.5rem',
    transition: 'all 0.2s'
  }
};

export default ProjectManager;
